<template>
  <!-- eslint-disable vue/no-v-html-->
  <wizard
    v-slot="{ currentStep }"
    ref="wizard"
    first-step="carePreferences"
    class="wizard-tso"
  >
    <wizard-step-intro>
      <h2 v-if="i18n.wizardSteps.wizardtso.title">
        {{ i18n.wizardSteps.wizardtso.title }}
      </h2>
      <slot name="wizard-tso-paragraph" />
    </wizard-step-intro>

    <wizard-step-indicator
      :steps="steps"
      :current-step="currentStep"
    />

    <wizard-step
      :i18n="i18n"
      name="carePreferences"
      :model="formData"
      next-step="myChild"
    >
      <wizard-step-intro>
        <h3>{{ i18n.wizardSteps.carePreference.title }}</h3>
      </wizard-step-intro>

      <fieldset class="grid grid-cols-12 sm:gap-6">
        <div class="col-span-12 md:col-span-4">
          <fieldset>
            <FormulateInput
              type="select"
              name="city"
              :options="cityOptions"
              :label="i18n.form.labels.selectCity"
              :placeholder="i18n.form.labels.select"
              validation="required"
              :validation-messages="i18n.form.validations.selectCity"
              @change="onchangeSchoolGroupCity"
            />
            <kf-loader
              v-if="isLoading"
              loader-size="sm"
              loader-color="primary"
            />
          </fieldset>
        </div>
        <div class="col-span-12 md:col-span-4">
          <fieldset>
            <FormulateInput
              type="select"
              name="school"
              :options="schoolOptions"
              :label="i18n.form.labels.selectSchool"
              :placeholder="i18n.form.labels.select"
              validation="required"
              :validation-messages="i18n.form.validations.selectSchool"
              :disabled="selectedCity === null"
              @change="onchangeSchoolGroupSchool"
            />
          </fieldset>
        </div>
        <div class="col-span-12 md:col-span-4">
          <fieldset>
            <FormulateInput
              type="select"
              name="group"
              :options="groupOptions"
              :label="i18n.form.labels.selectGroup"
              :placeholder="i18n.form.labels.select"
              validation="required"
              :validation-messages="i18n.form.validations.selectGroup"
              :disabled="selectedSchool === null"
              @change="onchangeSchoolGroupGroup"
            />
          </fieldset>
        </div>

        <div
          v-if="selectedGroup && selectedGroup.usersAreObligatedToTakeAllAvailableDays"
          class="col-span-12"
        >
          <p class="italic">
            {{ selectedGroup.textForWhenUsersAreObligatedToTakeAllAvailableDays }}
          </p>
        </div>

        <div class="col-span-12">
          <FormulateInput
            name="days[]"
            :label="i18n.form.labels.preferreddays"
            :options="dayOptions"
            type="checkbox"
            validation="required"
            :validation-messages="i18n.form.validations.days"
          />

          <FormulateInput
            name="bonusCode"
            type="text"
            label="bonus code"
            validation="nohtml"
            class="custom-form-element"
          />
          <FormulateInput
            name="website"
            type="text"
            label="website"
            validation="nohtml"
            class="custom-form-element"
          />

          <fields-date-picker
            field-name="startOfCare"
            :max-date="startOfCareMaxDate"
            :date-validation="startOfCareValidation"
            :i18n="i18n"
          />

          <FormulateInput
            name="alreadyClient"
            type="checkbox"
          >
            <template #label="{ id }">
              <label
                :for="id"
                v-html="i18n.form.labels.alreadyClient"
              />
            </template>
          </FormulateInput>
        </div>
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="myChild"
      :model="formData"
      previous-step="carePreferences"
      next-step="personalDetails"
    >
      <wizard-step-intro>
        <h3>{{ i18n.wizardSteps.myChild.title }}</h3>
      </wizard-step-intro>

      <fieldset class="grid grid-cols-12 sm:gap-6">
        <div class="col-span-12">
          <fields-gender
            :i18n="i18n"
            :only-allow-known-options="true"
          />
        </div>

        <div class="col-span-12 md:col-span-4">
          <FormulateInput
            :label="i18n.form.labels.childFirstName"
            name="firstName"
            validation="bail|required:trim|nohtml"
            :validation-messages="i18n.form.validations.childFirstName"
          />
        </div>
        <div class="col-span-12 md:col-span-3">
          <FormulateInput
            :label="i18n.form.labels.childNamePrefix"
            name="namePrefix"
            validation="nohtml"
            :validation-messages="i18n.form.validations.childNamePrefix"
          />
        </div>
        <div class="col-span-12 md:col-span-5">
          <FormulateInput
            :label="i18n.form.labels.childLastName"
            name="lastName"
            validation="bail|required:trim|nohtml"
            :validation-messages="i18n.form.validations.childLastName"
          />
        </div>

        <div class="col-span-12">
          <fields-date-picker
            field-name="dateOfBirth"
            :min-date="dobChildMinDate"
            :max-date="dobChildMaxDate"
            :date-validation="dobChildValidation"
            :i18n="i18n"
          />

          <div class="italic mt-4 mb-8">
            <slot name="wizard-tso-mychild-information" />
          </div>

          <FormulateInput
            name="remark"
            :label="i18n.form.labels.remark"
            type="textarea"
            validation="nohtml"
            :validation-messages="i18n.form.validations.remark"
          />
        </div>
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="personalDetails"
      :model="formData"
      previous-step="myChild"
      :next-step="submitPersonalDetails"
    >
      <wizard-step-intro>
        <h3>{{ i18n.wizardSteps.personalDetails.title }}</h3>
      </wizard-step-intro>

      <fieldset class="grid grid-cols-12 sm:gap-6">
        <div class="col-span-12">
          <FormulateInput
            name="salutation"
            :options="{
              male: i18n.form.labels.salutationOptions.male,
              female: i18n.form.labels.salutationOptions.female,
            }"
            type="radio"
            :label="i18n.form.labels.salutation"
            validation="required"
            :validation-messages="i18n.form.validations.salutation"
          />
        </div>

        <div class="col-span-12 md:col-span-4">
          <FormulateInput
            :label="i18n.form.labels.firstName"
            name="firstname"
            validation="bail|required:trim|nohtml"
            :validation-messages="i18n.form.validations.firstName"
          />
        </div>
        <div class="col-span-12 md:col-span-3">
          <FormulateInput
            :label="i18n.form.labels.prefix"
            name="prefix"
            validation="bail|nohtml"
            :validation-messages="i18n.form.validations.prefix"
          />
        </div>
        <div class="col-span-12 md:col-span-5">
          <FormulateInput
            :label="i18n.form.labels.lastName"
            name="lastname"
            validation="bail|required:trim|nohtml"
            :validation-messages="i18n.form.validations.lastName"
          />
        </div>

        <div class="col-span-12 md:col-span-6">
          <FormulateInput
            :label="i18n.form.labels.streetName"
            name="streetname"
            validation="bail|required:trim|nohtml"
            :validation-messages="i18n.form.validations.streetname"
          />
        </div>
        <div class="col-span-12 md:col-span-3">
          <FormulateInput
            :label="i18n.form.labels.houseNumber"
            name="housenumber"
            validation="bail|required:trim|nohtml"
            :validation-messages="i18n.form.validations.houseNumber"
          />
        </div>
        <div class="col-span-12 md:col-span-3">
          <FormulateInput
            :label="i18n.form.labels.suffix"
            name="suffix"
            validation="bail|nohtml"
            :validation-messages="i18n.form.validations.suffix"
          />
        </div>

        <div class="col-span-12 md:col-span-6">
          <FormulateInput
            name="zipcode"
            type="text"
            :label="i18n.form.labels.zipcode"
            validation="bail|required:trim|zipcode|nohtml"
            :validation-messages="i18n.form.validations.zipcode"
          />
        </div>

        <div class="col-span-12 md:col-span-6">
          <FormulateInput
            name="residence"
            type="text"
            :label="i18n.form.labels.residence"
            validation="bail|required:trim|nohtml"
            :validation-messages="i18n.form.validations.residence"
          />
        </div>

        <div class="col-span-12 md:col-span-6">
          <FormulateInput
            name="email"
            type="email"
            :label="i18n.form.labels.email"
            validation="bail|required:trim|email"
            :validation-messages="i18n.form.validations.email"
          />
        </div>

        <div class="col-span-12 md:col-span-6">
          <FormulateInput
            name="phoneNumber"
            type="text"
            :label="i18n.form.labels.phoneNumber"
            validation="bail|required:trim|phonenumber|nohtml"
            :validation-messages="i18n.form.validations.phoneNumber"
          />
        </div>

        <div class="col-span-12">
          <fields-date-picker
            field-name="firstParentDateOfBirth"
            :min-date="dobParentMinDate"
            :max-date="dobParentMaxDate"
            :date-validation="dobParentValidation"
            :i18n="i18n"
          />

          <FormulateInput
            name="twoParents"
            :options="{
              yes: i18n.form.labels.twoParentsOptions.twoParents,
              no: i18n.form.labels.twoParentsOptions.oneParent,
            }"
            type="radio"
            :label="i18n.form.labels.twoParents"
            validation="required"
            :validation-messages="i18n.form.validations.twoParents"
            @input="toggleSecondParentFields"
          />
        </div>
      </fieldset>

      <transition name="slide-down">
        <fieldset
          v-if="showSecondParentFields"
          class="grid grid-cols-12 sm:gap-6"
        >
          <div class="col-span-12">
            <FormulateInput
              name="secondParentSalutation"
              :options="{
                male: i18n.form.labels.salutationOptions.male,
                female: i18n.form.labels.salutationOptions.female,
              }"
              type="radio"
              :label="i18n.form.labels.salutation"
              validation="required"
              :validation-messages="i18n.form.validations.salutation"
            />
          </div>

          <div class="col-span-12 md:col-span-4">
            <FormulateInput
              :label="i18n.form.labels.firstName"
              name="secondParentFirstname"
              validation="bail|required:trim|nohtml"
              :validation-messages="i18n.form.validations.firstName"
            />
          </div>
          <div class="col-span-12 md:col-span-3">
            <FormulateInput
              :label="i18n.form.labels.prefix"
              name="secondParentPrefix"
              validation="bail|nohtml"
              :validation-messages="i18n.form.validations.prefix"
            />
          </div>
          <div class="col-span-12 md:col-span-5">
            <FormulateInput
              :label="i18n.form.labels.lastName"
              name="secondParentLastname"
              validation="bail|required:trim|nohtml"
              :validation-messages="i18n.form.validations.lastName"
            />
          </div>

          <div class="col-span-12 md:col-span-6">
            <FormulateInput
              :label="i18n.form.labels.streetName"
              name="secondParentStreetname"
              validation="bail|required:trim|nohtml"
              :validation-messages="i18n.form.validations.streetname"
            />
          </div>
          <div class="col-span-12 md:col-span-3">
            <FormulateInput
              :label="i18n.form.labels.houseNumber"
              name="secondParentHousenumber"
              validation="bail|required:trim|nohtml"
              :validation-messages="i18n.form.validations.houseNumber"
            />
          </div>
          <div class="col-span-12 md:col-span-3">
            <FormulateInput
              :label="i18n.form.labels.suffix"
              name="secondParentSuffix"
              validation="bail|nohtml"
              :validation-messages="i18n.form.validations.suffix"
            />
          </div>

          <div class="col-span-12 md:col-span-6">
            <FormulateInput
              name="secondParentZipcode"
              type="text"
              :label="i18n.form.labels.zipcode"
              validation="bail|required:trim|zipcode|nohtml"
              :validation-messages="i18n.form.validations.zipcode"
            />
          </div>

          <div class="col-span-12 md:col-span-6">
            <FormulateInput
              name="secondParentResidence"
              type="text"
              :label="i18n.form.labels.residence"
              validation="bail|required:trim|nohtml"
              :validation-messages="i18n.form.validations.residence"
            />
          </div>

          <div class="col-span-12 md:col-span-6">
            <FormulateInput
              name="secondParentEmail"
              type="email"
              :label="i18n.form.labels.email"
              validation="bail|required:trim|email"
              :validation-messages="i18n.form.validations.email"
            />
          </div>

          <div class="col-span-12 md:col-span-6">
            <FormulateInput
              name="secondParentPhoneNumber"
              type="text"
              :label="i18n.form.labels.phoneNumber"
              validation="bail|required:trim|phonenumber|nohtml"
              :validation-messages="i18n.form.validations.phoneNumber"
            />
          </div>

          <div class="col-span-12">
            <fields-date-picker
              field-name="secondParentDateOfBirth"
              :min-date="dobParentMinDate"
              :max-date="dobParentMaxDate"
              :date-validation="dobParentValidation"
              :i18n="i18n"
            />
          </div>
        </fieldset>
      </transition>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="payment"
      :model="formData"
      next-step="summary"
      previous-step="personalDetails"
    >
      <wizard-step-intro>
        <h3>{{ i18n.wizardSteps.payment.title }}</h3>
      </wizard-step-intro>

      <fieldset class="grid grid-cols-12 sm:gap-6">
        <div class="col-span-12">
          <FormulateInput
            name="automaticCollection"
            :options="{
              yes: i18n.form.labels.automaticCollectionOptions.yes,
              no: i18n.form.labels.automaticCollectionOptions.no,
            }"
            type="radio"
            :label="i18n.form.labels.automaticCollection"
            validation="required"
            :validation-messages="i18n.form.validations.automaticCollection"
          />

          <div class="mt-4 mb-8">
            <slot name="wizard-tso-payment-twikey" />
          </div>

          <FormulateInput
            name="agree"
            type="checkbox"
            validation="required"
            :validation-messages="i18n.form.validations.agree"
          >
            <template #label="{ id }">
              <label
                :for="id"
                v-html="i18n.form.labels.agree"
              />
            </template>
          </FormulateInput>

          <div class="mt-4 mb-8">
            <slot name="wizard-tso-payment-info" />
          </div>
        </div>
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="summary"
      :model="formData"
      :next-step="sendRequest"
      previous-step="payment"
      :submit-button-text="i18n.form.buttons.submit"
      :is-submitting="request.isSending"
      :error-submitting="request.errorSending"
    >
      <wizard-step-intro>
        <h3>{{ i18n.wizardSteps.summary.title }}</h3>
        <slot name="wizard-tso-summary-paragraph" />
        <p class="text-sm italic">
          {{ i18n.form.labels.summaryCostsExplanation.replace('{costsPerMonth}', monthlyCosts.toFixed(2).toString().replace('.', ',')) }}
        </p>
      </wizard-step-intro>

      <table class="wizard-summary mb-8">
        <tbody>
          <tr>
            <td>
              <h3>{{ i18n.form.steps.carePreferences }}</h3>
            </td>
            <td>
              <button
                class="btn btn-wizard-summary-edit"
                @click.prevent="goToStep('carePreferences')"
                @keyup.prevent="goToStep('carePreferences')"
              >
                {{ i18n.form.labels.summaryEdit }}
              </button>
            </td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryCity }}
            </td>
            <td>{{ formData.carePreferences.city }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryLocation }}
            </td>
            <td>{{ locationName }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summarySchool }}
            </td>
            <td>{{ formData.carePreferences.school }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryGroup }}
            </td>
            <td>{{ formData.carePreferences.group }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryDays }}
            </td>
            <td>{{ daysOfCareTranslatedString }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryStartDate }}
            </td>
            <td>{{ formData.carePreferences.startOfCare }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryAlreadyClient }}
            </td>
            <td>{{ alreadyClientString }}</td>
          </tr>

          <tr>
            <td
              colspan="2"
              class="pb-8"
            >
              &nbsp;
            </td>
          </tr>

          <tr>
            <td>
              <h3>
                {{ i18n.form.steps.myChild }}
              </h3>
            </td>
            <td>
              <button
                class="btn btn-wizard-summary-edit"
                @click.prevent="goToStep('myChild')"
                @keyup.prevent="goToStep('myChild')"
              >
                {{ i18n.form.labels.summaryEdit }}
              </button>
            </td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryChildGender }}
            </td>
            <td>{{ childGender }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryChildFirstname }}
            </td>
            <td>{{ formData.myChild.firstName }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryChildPrefix }}
            </td>
            <td>{{ formData.myChild.namePrefix }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryChildLastname }}
            </td>
            <td>{{ formData.myChild.lastName }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryChildDateOfBirth }}
            </td>
            <td>{{ formData.myChild.dateOfBirth }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryChildRemark }}
            </td>
            <td>{{ formData.myChild.remark }}</td>
          </tr>

          <tr>
            <td
              colspan="2"
              class="pb-8"
            >
              &nbsp;
            </td>
          </tr>

          <tr class="mt-8">
            <td>
              <h3>
                {{ i18n.form.steps.personalDetails }}
              </h3>
            </td>
            <td>
              <button
                class="btn btn-wizard-summary-edit"
                @click.prevent="goToStep('personalDetails')"
                @keyup.prevent="goToStep('personalDetails')"
              >
                {{ i18n.form.labels.summaryEdit }}
              </button>
            </td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryTitle }}
            </td>
            <td>{{ getSalutation(formData.personalDetails.salutation) }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryFirstname }}
            </td>
            <td>{{ formData.personalDetails.firstname }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryPrefix }}
            </td>
            <td>{{ formData.personalDetails.prefix }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryLastname }}
            </td>
            <td>{{ formData.personalDetails.lastname }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryDateOfBirth }}
            </td>
            <td>{{ formData.personalDetails.firstParentDateOfBirth }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryStreetname }}
            </td>
            <td>{{ formData.personalDetails.streetname }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryHousenumber }}
            </td>
            <td>{{ formData.personalDetails.housenumber }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summarySuffix }}
            </td>
            <td>{{ formData.personalDetails.suffix }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryZipcode }}
            </td>
            <td>{{ formData.personalDetails.zipcode }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryResidence }}
            </td>
            <td>{{ formData.personalDetails.residence }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryEmail }}
            </td>
            <td>{{ formData.personalDetails.email }}</td>
          </tr>
          <tr>
            <td class="font-bold">
              {{ i18n.form.labels.summaryTelephoneNumber }}
            </td>
            <td>{{ formData.personalDetails.phoneNumber }}</td>
          </tr>

          <tr v-if="showSecondParentFields">
            <td
              colspan="2"
              class="pb-8"
            >
              &nbsp;
            </td>
          </tr>

          <tr v-if="showSecondParentFields">
            <td>
              <h3>
                {{ i18n.form.labels.summarySecondParent }}
              </h3>
            </td>
            <td>&nbsp;</td>
          </tr>
          <tr v-if="showSecondParentFields">
            <td class="font-bold">
              {{ i18n.form.labels.summaryTitle }}
            </td>
            <td>{{ getSalutation(formData.personalDetails.secondParentSalutation) }}</td>
          </tr>
          <tr v-if="showSecondParentFields">
            <td class="font-bold">
              {{ i18n.form.labels.summaryFirstname }}
            </td>
            <td>{{ formData.personalDetails.secondParentFirstname }}</td>
          </tr>
          <tr v-if="showSecondParentFields">
            <td class="font-bold">
              {{ i18n.form.labels.summaryPrefix }}
            </td>
            <td>{{ formData.personalDetails.secondParentPrefix }}</td>
          </tr>
          <tr v-if="showSecondParentFields">
            <td class="font-bold">
              {{ i18n.form.labels.summaryLastname }}
            </td>
            <td>{{ formData.personalDetails.secondParentLastname }}</td>
          </tr>
          <tr v-if="showSecondParentFields">
            <td class="font-bold">
              {{ i18n.form.labels.summaryDateOfBirth }}
            </td>
            <td>{{ formData.personalDetails.secondParentDateOfBirth }}</td>
          </tr>
          <tr v-if="showSecondParentFields">
            <td class="font-bold">
              {{ i18n.form.labels.summaryStreetname }}
            </td>
            <td>{{ formData.personalDetails.secondParentStreetname }}</td>
          </tr>
          <tr v-if="showSecondParentFields">
            <td class="font-bold">
              {{ i18n.form.labels.summaryHousenumber }}
            </td>
            <td>{{ formData.personalDetails.secondParentHousenumber }}</td>
          </tr>
          <tr v-if="showSecondParentFields">
            <td class="font-bold">
              {{ i18n.form.labels.summarySuffix }}
            </td>
            <td>{{ formData.personalDetails.secondParentSuffix }}</td>
          </tr>
          <tr v-if="showSecondParentFields">
            <td class="font-bold">
              {{ i18n.form.labels.summaryZipcode }}
            </td>
            <td>{{ formData.personalDetails.secondParentZipcode }}</td>
          </tr>
          <tr v-if="showSecondParentFields">
            <td class="font-bold">
              {{ i18n.form.labels.summaryResidence }}
            </td>
            <td>{{ formData.personalDetails.secondParentResidence }}</td>
          </tr>
          <tr v-if="showSecondParentFields">
            <td class="font-bold">
              {{ i18n.form.labels.summaryEmail }}
            </td>
            <td>{{ formData.personalDetails.secondParentEmail }}</td>
          </tr>
          <tr v-if="showSecondParentFields">
            <td class="font-bold">
              {{ i18n.form.labels.summaryTelephoneNumber }}
            </td>
            <td>{{ formData.personalDetails.secondParentPhoneNumber }}</td>
          </tr>
        </tbody>
      </table>
    </wizard-step>

    <slot name="wizard-tso-footer" />
  </wizard>
</template>

<script>
import FieldsDatePicker from '@/components/forms/fields/FieldsDatePicker.vue';
import FieldsGender from '@/components/forms/fields/FieldsGender.vue';
import KfLoader from '@/components/loader/Loader';
import Wizard from '@/components/wizards/components/Wizard.vue';
import WizardStep from '@/components/wizards/components/WizardStep.vue';
import WizardStepIndicator from '@/components/wizards/components/WizardStepIndicator.vue';
import WizardStepIntro from '@/components/wizards/components/WizardStepIntro.vue';
import getQueryParams from '@/lib/formulate/getFormQueryParam';
import Utils from '@/lib/utils';
import axios from 'axios';
import moment from 'moment';

export default {
  name: 'WizardTso',
  components: {
    Wizard,
    WizardStep,
    WizardStepIntro,
    WizardStepIndicator,
    FieldsDatePicker,
    FieldsGender,
    KfLoader,
  },
  props: {
    i18n: {
      type: Object,
      default: null,
    },
    prefillData: {
      type: Object,
      default: () => { },
    },
    locale: {
      type: String,
      default: () => 'nl',
    },
    label: {
      type: String,
      default: 'Partou',
    },
    successPageUrl: {
      type: String,
      default: () => '/',
    },
    apiHostName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      formData: {
        carePreferences: {},
        myChild: {},
        personalDetails: this.$store.state.wizard.personalDetails,
        payment: {},
      },
      schoolGroupData: [],
      cityOptions: [],
      selectedCity: null,
      schoolOptions: [],
      selectedSchool: null,
      groupOptions: [],
      selectedGroup: null,
      dayOptions: [{
        value: 'monday',
        label: this.i18n.form.labels?.daysoptions?.monday,
        disabled: true,
        class: 'is-disabled',
      },{
        value: 'tuesday',
        label: this.i18n.form.labels?.daysoptions?.tuesday,
        disabled: true,
        class: 'is-disabled',
      },{
        value: 'wednesday',
        label: this.i18n.form.labels?.daysoptions?.wednesday,
        disabled: true,
        class: 'is-disabled',
      },{
        value: 'thursday',
        label: this.i18n.form.labels?.daysoptions?.thursday,
        disabled: true,
        class: 'is-disabled',
      },{
        value: 'friday',
        label: this.i18n.form.labels?.daysoptions?.friday,
        disabled: true,
        class: 'is-disabled',
      }],
      request: {
        isSending: false,
        errorSending: false,
      },
      isLoading: false,
      showSecondParentFields: null,
      steps: [{
        key: 'carePreferences',
        name: this.i18n.form.steps.carePreferences,
        completed: false,
        active: false,
      }, {
        key: 'myChild',
        name: this.i18n.form.steps.myChild,
        completed: false,
        active: false,
      }, {
        key: 'personalDetails',
        name: this.i18n.form.steps.personalDetails,
        completed: false,
        active: false,
      }, {
        key: 'payment',
        name: this.i18n.form.steps.payment,
        completed: false,
        active: false,
      }, {
        key: 'summary',
        name: this.i18n.form.steps.summary,
        completed: true,
        active: false,
      }],
    };
  },
  computed: {
    startOfCareMaxDate() {
      return `${Utils.date.getDate(moment().add(10, 'years').toDate())}`;
    },
    dobParentMinDate() {
      return `${Utils.date.getDate(moment().subtract(113, 'years').toDate())}`;
    },
    dobParentMaxDate() {
      return `${Utils.date.getDate(moment().subtract(1, 'days').toDate())}`;
    },
    dobChildMinDate() {
      return `${Utils.date.getDate(moment().subtract(13, 'years').toDate())}`;
    },
    dobChildMaxDate() {
      return `${Utils.date.getDate(moment().subtract(3, 'years').toDate())}`;
    },
    startOfCareValidation() {
      return `bail|required|before:${this.startOfCareMaxDate}`;
    },
    dobParentValidation() {
      return `bail|required|after:${this.dobParentMinDate}|before:${this.dobParentMaxDate}`;
    },
    dobChildValidation() {
      return `bail|required|before:${this.dobChildMaxDate}|after:${this.dobChildMinDate}`;
    },
    locationName() {
      return this.selectedSchool?.locationName || '';
    },
    daysOfCareTranslatedString() {
      let str = null;
      const days = this.formData?.carePreferences?.['days[]'] || [];
      days.forEach((day) => {
        if (!str) {
          str = `${this.i18n.form.labels.daysoptions[day]}`;
        } else {
          str = `${str}, ${this.i18n.form.labels.daysoptions[day]}`;
        }
      });
      return str;
    },
    alreadyClientString() {
      const isClient = this.formData.carePreferences?.alreadyClient ? 'yes' : 'no';
      return this.i18n.form.labels.alreadyClientOptions[isClient];
    },
    childGender() {
      return this.i18n.form.labels.genderoptions[this.formData.myChild.gender];
    },
    monthlyCosts() {
      return (this.selectedSchool?.dayRatePerMonth || 0) * this.amountOfSelectedDays;
    },
    amountOfSelectedDays() {
      return this.formData?.carePreferences?.['days[]']?.length || 0;
    },
  },
  async created() {
        this.formData.personalDetails = {
      ...this.formData.personalDetails,
      ...getQueryParams(this.prefillData)
    }
    // fetch school group data to populate dropdowns in step 1
    this.schoolGroupData = await this.getSchoolGroups();
    this.cityOptions = this.schoolGroupData.cities.map((city) => city.name).sort();
  },
  methods: {
    submitPersonalDetails() {
      this.$store.dispatch('wizard/setPersonalDetails', this.formData.personalDetails);
      return 'payment'
    },
    getSchoolGroups() {
      return new Promise((resolve, reject) => {
        let config = {};
        this.isLoading = true;

        const requestVerificationToken = document.body.querySelector('input[type=hidden][name=__RequestVerificationToken]')?.value;
        if (requestVerificationToken) {
          config = {
            headers: {
              'X-REQUEST-VERIFICATION-TOKEN': requestVerificationToken,
            }
          };
        }

        axios
          .get(`${this.apiHostName}/umbraco/api/TSO/SchoolGroups/ByCityAndSchool`, config)
          .then((response) => {
            resolve(response.data);
          }).catch(error => {
            reject(error);
          })
          .finally(() => {
            this.isLoading = false
          });
      });
    },
    onchangeSchoolGroupCity(e) {
      this.selectedCity = this.schoolGroupData.cities.find((city) => {
        return city.name === e.target.value;
      });
      this.formData.carePreferences.school = null;
      this.formData.carePreferences.group = null;
      this.formData.carePreferences['days[]'] = [];
      this.disableDaysOptions();
      this.schoolOptions = this.selectedCity.schools.map((school) => school.name);
    },
    onchangeSchoolGroupSchool(e) {
      this.selectedSchool = this.selectedCity.schools.find((school) => {
        return school.name === e.target.value;
      });
      this.formData.carePreferences.group = null;
      this.formData.carePreferences['days[]'] = [];
      this.disableDaysOptions();
      this.groupOptions = this.selectedSchool.groups.map((group) => group.name);
    },
    onchangeSchoolGroupGroup(e) {
      this.selectedGroup = this.selectedSchool.groups.find((group) => {
        return group.name === e.target.value;
      });

      this.dayOptions = [{
        value: 'monday',
        label: this.i18n.form.labels?.daysoptions?.monday,
        disabled: !this.selectedGroup.days?.monday || this.selectedGroup.usersAreObligatedToTakeAllAvailableDays,
        class: !this.selectedGroup.days?.monday || this.selectedGroup.usersAreObligatedToTakeAllAvailableDays ? 'is-disabled': '',
      },{
        value: 'tuesday',
        label: this.i18n.form.labels?.daysoptions?.tuesday,
        disabled: !this.selectedGroup.days?.tuesday || this.selectedGroup.usersAreObligatedToTakeAllAvailableDays,
        class: !this.selectedGroup.days?.tuesday || this.selectedGroup.usersAreObligatedToTakeAllAvailableDays ? 'is-disabled': '',
      },{
        value: 'wednesday',
        label: this.i18n.form.labels?.daysoptions?.wednesday,
        disabled: !this.selectedGroup.days?.wednesday || this.selectedGroup.usersAreObligatedToTakeAllAvailableDays,
        class: !this.selectedGroup.days?.wednesday || this.selectedGroup.usersAreObligatedToTakeAllAvailableDays ? 'is-disabled': '',
      },{
        value: 'thursday',
        label: this.i18n.form.labels?.daysoptions?.thursday,
        disabled: !this.selectedGroup.days?.thursday || this.selectedGroup.usersAreObligatedToTakeAllAvailableDays,
        class: !this.selectedGroup.days?.thursday || this.selectedGroup.usersAreObligatedToTakeAllAvailableDays ? 'is-disabled': '',
      },{
        value: 'friday',
        label: this.i18n.form.labels?.daysoptions?.friday,
        disabled: !this.selectedGroup.days?.friday || this.selectedGroup.usersAreObligatedToTakeAllAvailableDays,
        class: !this.selectedGroup.days?.friday || this.selectedGroup.usersAreObligatedToTakeAllAvailableDays ? 'is-disabled': '',
      }];

      this.formData.carePreferences['days[]'] =
        Object.keys(this.selectedGroup.days)
          .filter(dayName => this.selectedGroup.days[dayName] && this.selectedGroup.usersAreObligatedToTakeAllAvailableDays);
    },
    disableDaysOptions() {
      this.dayOptions.forEach((day) => {
        day.disabled = true;
        day.class = 'is-disabled';
      });
    },
    toggleSecondParentFields(value) {
      this.showSecondParentFields = (value === 'yes') ? true : false;

      if (this.showSecondParentFields) {
        this.formData.personalDetails.secondParentPrefix = this.formData.personalDetails.prefix;
        this.formData.personalDetails.secondParentLastname = this.formData.personalDetails.lastname;
        this.formData.personalDetails.secondParentStreetname = this.formData.personalDetails.streetname;
        this.formData.personalDetails.secondParentHousenumber = this.formData.personalDetails.housenumber;
        this.formData.personalDetails.secondParentSuffix = this.formData.personalDetails.suffix;
        this.formData.personalDetails.secondParentZipcode = this.formData.personalDetails.zipcode;
        this.formData.personalDetails.secondParentResidence = this.formData.personalDetails.residence;
      }
    },
    getSalutation(key) {
      return this.i18n.form.labels.salutationOptions?.[key] || '';
    },
    goToStep(stepName) {
      this.$refs.wizard.goToStep(stepName);
    },
    sendRequest() {
      this.request.errorSending = false;
      this.request.isSending = true;

      const requestVerificationToken = document.body.querySelector('input[type=hidden][name=__RequestVerificationToken]')?.value;

      const config = {
        headers: {
          'X-REQUEST-VERIFICATION-TOKEN': requestVerificationToken,
        }
      };

      const dataToPost = {
        city:	this.formData.carePreferences.city,
        school:	this.formData.carePreferences.school,
        locationName: this.locationName,
        locationEmailAddress:  this.selectedSchool?.locationEmailAddress || '',
        locationTelephoneNumber: this.selectedSchool?.locationTelephoneNumber || '',
        locationManager:  this.selectedSchool?.locationManager || '',
        isPartouLocation: this.selectedSchool?.locationOrganisationLabel === 'Partou' ? true : false,
        dayRatePerMonth: this.selectedSchool?.dayRatePerMonth || '00.00',
        group: this.formData.carePreferences.group,
        days: this.formData.carePreferences['days[]'],
        website: this.formData.carePreferences.website || '',
        bonusCode: this.formData.carePreferences.bonusCode || '',
        startDate: this.formData.carePreferences.startOfCare,
        isClient: this.formData.carePreferences?.alreadyClient ? true : false,
        child:	{
          gender: this.childGender,
          firstName: this.formData.myChild?.firstName || '',
          preposition: this.formData.myChild?.namePrefix || '',
          surname: this.formData.myChild.lastName,
          dateOfBirth: this.formData.myChild.dateOfBirth,
          comments: this.formData.myChild?.remark || '',
          fullName: `${this.formData.myChild?.firstName || ''} ${this.formData.myChild?.namePrefix || ''} ${this.formData.myChild.lastName}`.trim(),
          dateOfBirthAsString: this.formData.myChild.dateOfBirth,
        },
        parents:	[{
          title: this.formData.personalDetails.salutation,
          firstName: this.formData.personalDetails.firstname,
          preposition: this.formData.personalDetails?.prefix || '',
          surname: this.formData.personalDetails.lastname,
          address: {
            street: this.formData.personalDetails.streetname,
            houseNumber: this.formData.personalDetails.housenumber,
            addition: this.formData.personalDetails.suffix,
            postalCode: this.formData.personalDetails.zipcode,
            city: this.formData.personalDetails.residence,
          },
          emailAddress: this.formData.personalDetails.email,
          telephoneNumber: this.formData.personalDetails?.phoneNumber || '',
          dateOfBirth: this.formData.personalDetails.firstParentDateOfBirth,
          fullName: `${this.formData.personalDetails?.firstname || ''} ${this.formData.personalDetails?.prefix || ''} ${this.formData.personalDetails.lastname}`.trim(),
        }],
        agreeToAutomaticPayments: this.formData.payment.automaticCollection === 'yes' ? true : false,
        acceptTermsAndConditions: this.formData.payment.agree,
        locale: this.locale,
      };

      if (this.formData.personalDetails.twoParents === 'yes') {
        dataToPost.parents.push({
          title: this.formData.personalDetails.secondParentSalutation,
          firstName: this.formData.personalDetails.secondParentFirstname,
          preposition: this.formData.personalDetails?.secondParentPrefix || '',
          surname: this.formData.personalDetails.secondParentLastname,
          address: {
            street: this.formData.personalDetails.secondParentStreetname,
            houseNumber: this.formData.personalDetails.secondParentHousenumber,
            addition: this.formData.personalDetails.secondParentSuffix,
            postalCode: this.formData.personalDetails.secondParentZipcode,
            city: this.formData.personalDetails.secondParentResidence,
          },
          emailAddress: this.formData.personalDetails.secondParentEmail,
          telephoneNumber: this.formData.personalDetails?.secondParentPhoneNumber || '',
          dateOfBirth: this.formData.personalDetails.secondParentDateOfBirth,
          fullName: `${this.formData.personalDetails?.secondParentFirstname || ''} ${this.formData.personalDetails?.secondParentPrefix || ''} ${this.formData.personalDetails.secondParentLastname}`.trim(),
        });
      }

      axios
        .post(`${this.apiHostName}/umbraco/api/TsoApplicationForm/Submit`, dataToPost, config)
        .then(() => {
          window.location.href = this.successPageUrl;
        })
        .catch(() => {
          this.request.isSending = false;
          this.request.errorSending = true;
        });
    },
  }
};
</script>
